import { useCallback, useEffect, useState } from "react";
import type { TenantMe } from "@am/shared";
import { useApiClient } from "../api/client";

interface UseMeResult {
  me: TenantMe | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

// Lightweight hook for /tenant/api/me. Each consumer fetches independently
// — the endpoint is cheap and we don't want stale data after a save.
export function useMe(): UseMeResult {
  const api = useApiClient();
  const [me, setMe] = useState<TenantMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api<TenantMe>("/tenant/api/me");
      setMe(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load profile"));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  return { me, loading, error, refresh: load };
}
